import { getCookie } from '@/modules/cookieReader'
import { sanitize } from '@/modules/sanitizeHtml'
import { isEmptyObject } from '@/modules/objectHelpers'

export const strict = false

export const state = () => ({
  brand: '',
  config: {},
  route: {},
  story: {
    content: {
      component: null
    }
  },
  step: { offers: [] },
  googleCategory: null,
  slug: null,
  paymentToken: null,
  orders: [],
  error: null,
  paymentError: null,
  purchaseAttempts: 0,
  busy: false,
  smsOptIn: false,
  customer: {
    firstName: 'Fellow Patriot',
    lastName: '',
    email: '',
    phone: '',
    billingInfo: {
      address: '',
      city: '',
      country: 'US',
      state: '',
      zip: ''
    },
    shippingInfo: {
      address: '',
      city: '',
      country: 'US',
      state: '',
      zip: ''
    },
    shippingAddressDifferent: false,
    smsConsent: false
  },
  queryParameters: {},
  queryString: '',
  sessionId: null,
  ecommerce: undefined,
  // 'full-pay' | 'splitit-plan' | 'sticky-plan'. Defaulted to 'full-pay'
  paymentMode: 'full-pay',
  // True only while a splitit checkout is mounted; lets shared components
  // opt into splitit-aware pricing. Stays false for legacy funnels.
  splititCheckoutMounted: false
})

export const getters = {
  offer: state => productId => {
    let matchingOffer = {
      product: {
        quantity: null,
        displayName: null,
        campaignId: null,
        id: null
      },
      shipping: {
        internationalPrice: null,
        internationalId: null,
        domesticPrice: null,
        domesticId: null,
        exclusions: []
      },
      payments: {
        numberOfPayments: null,
        paymentModalCheckboxes: [],
        amountFinanced: null,
        financeCharge: null,
        annualRate: null,
        totalOfPayments: null,
        paymentPlanFor: null,
        paymentAmount: null,
        totalSalesPrice: null
      },
      name: null,
      pricing: {
        offerPrice: null,
        originalPrice: null,
        paymentCategory: null,
        displayPrice: null,
        isPaymentPlan: false,
        overridePrice: null,
        splititOverridePrice: null
      },
      accept: null
    }

    if (parseInt(productId) > 0) {
      productId = parseInt(productId)
      state.step.offers.forEach(offer => {
        if (offer.product.id === productId) {
          matchingOffer = offer
        }
      })
    }
    return matchingOffer
  },
  inEditor: state =>
    process.browser && window.location !== window.parent.location,
  offers: state => state.step.offers,
  orders: state => state.orders.map(order => order.productId),
  pricing: (state, getters) => (productId, quantity) => {
    quantity = quantity || 1
    const emptyPricing = {
      originalPrice: null,
      price: null,
      quantity: null,
      shippingTotal: null,
      subTotal: null,
      grandTotal: null,
      overridePrice: null
    }
    if (!productId) {
      return emptyPricing
    }

    const offer = getters.offer(productId)

    if (Object.keys(offer).length === 0) {
      return emptyPricing
    }
    const price = offer.pricing.displayPrice
    const originalPrice = offer.pricing.originalPrice * quantity
    const overridePrice = offer.pricing.overridePrice

    const shippingTotal =
      state.customer.shippingInfo.country !== 'US'
        ? offer.shipping.internationalPrice
        : offer.shipping.domesticPrice

    const subTotal = price * quantity
    const grandTotal = subTotal + shippingTotal

    return {
      originalPrice,
      price,
      quantity,
      shippingTotal,
      subTotal,
      grandTotal,
      overridePrice
    }
  },
  customer: state => () => state.customer,
  extraShipping: state =>
    state.customer.shippingInfo.state === 'AK' ||
    state.customer.shippingInfo.state === 'HI',
  confirmedPurchase: state => productId => {
    let pidFound = false
    state.orders.forEach(order => {
      if (
        parseInt(order.productId) === parseInt(productId) &&
        order.slug === state.slug
      ) {
        pidFound = true
      }
    })
    return pidFound
  },
  queryParameters: state => state.queryParameters,
  queryString: state => state.queryString,
  error: state => state.error,
  story: state => state.story,
  paymentError: state => state.paymentError,
  purchaseAttempts: state => state.purchaseAttempts,
  busy: state => state.busy,
  sessionId: state => state.sessionId,
  smsOptIn: state => state.smsOptIn,
  route: state => state.route,
  stepType: state => state.step.type,
  isTestOrder: state =>
    state.customer.email.includes('@test.com') ||
    state.customer.lastName.toLowerCase() === 'test' ||
    state.customer.billingInfo.address.toLowerCase() === 'noship' ||
    state.customer.shippingInfo.address.toLowerCase() === 'noship',
  config: state => state.config,
  paymentMode: state => state.paymentMode,
  isSplititPayment: state => state.paymentMode === 'splitit-plan',
  isSplititCheckoutMounted: state => state.splititCheckoutMounted,
  // Charged price for an offer given the active payment method. Splitit-plan
  // uses splititOverridePrice when present; otherwise overridePrice ||
  // displayPrice. Pass { forSplitit } to preview a not-yet-active mode.
  coalescedPrice: (state, getters) => (offer, opts) => {
    const pricing = (offer && offer.pricing) || {}
    const useSplitit =
      opts && typeof opts.forSplitit === 'boolean'
        ? opts.forSplitit
        : getters.isSplititPayment
    if (useSplitit) {
      return (
        pricing.splititOverridePrice ||
        pricing.overridePrice ||
        pricing.displayPrice ||
        0
      )
    }
    return pricing.overridePrice || pricing.displayPrice || 0
  }
}

export const mutations = {
  SET_PAGE(state, route) {
    state.busy = false
    state.route = route
    state.ecommerce = undefined
  },
  setContext(state, config) {
    state.brand = config.brand
    state.config = config
  },
  enterStory(state, story) {
    state.story = story
    state.slug = story.full_slug
  },
  enterStep(state, step) {
    state.paymentError = state.error = null
    state.purchaseAttempts = 0
    state.step = step
    // Step transitions reset the active payment mode. A non-splitit step
    // shouldn't inherit a splitit-plan flag from the previous one.
    state.paymentMode = 'full-pay'
    state.splititCheckoutMounted = false
  },
  setPaymentMode(state, mode) {
    state.paymentMode = mode || 'full-pay'
  },
  setSplititCheckoutMounted(state, mounted) {
    state.splititCheckoutMounted = !!mounted
  },
  setGoogleCategory(state, googleCategory) {
    state.googleCategory = googleCategory
  },
  setPaymentToken(state, paymentToken) {
    state.paymentToken = paymentToken
  },
  CLEAR_PAYMENT_TOKEN(state) {
    state.paymentToken = null
  },
  setError(state, error) {
    state.error = error
  },
  setPaymentError(state, error) {
    state.paymentError = sanitize(error)
  },
  updatePhone(state, phone) {
    state.customer.phone = phone
  },
  addOrder(state, result) {
    delete result.creditCardInfo
    state.orders.push(result)
    const livePurchase = !/@(test\.com|4patriots\.com)/gi.test(
      state.customer.email
    )
    const afid = state.queryParameters && state.queryParameters.afid
    const affString = afid ? ' (' + afid + ')' : ''
    state.ecommerce = {
      purchase: {
        actionField: {
          id: result.orderId,
          affiliation:
            state.config.brand === 'PAT'
              ? '4Patriots' + affString
              : 'Patriot Health Alliance' + affString,
          currency: 'USD',
          revenue: result.pricing.grandTotal,
          tax: result.pricing.taxTotal,
          shipping: result.pricing.shippingTotal,
          livePurchase: livePurchase,
          promoCode: result.promoCode
        },
        products: [
          {
            name: result.offer.name,
            item_name: result.offer.name,
            id: result.offer.product.id,
            item_id: result.offer.product.id,
            affiliation:
              state.config.brand === 'PAT'
                ? '4Patriots' + affString
                : 'Patriot Health Alliance' + affString,
            price: result.offer.pricing.displayPrice,
            brand: state.config.brand,
            category: state.route.productLine,
            dimension1: state.route.funnel,
            dimension2: state.step.type,
            variant: result.offer.pricing.paymentCategory,
            quantity: result.quantity,
            promoCode: result.promoCode,
            metric1: result.offer.pricing.metric1,
            orderSalesTaxAmount: result.orderSalesTaxAmount
          }
        ]
      },
      customer: {
        email: state.customer.email,
        phone: state.customer.phone,
        firstName: state.customer.firstName,
        lastName: state.customer.lastName,
        zip: state.customer.billingInfo.zip,
        state: state.customer.billingInfo.state,
        city: state.customer.billingInfo.city,
        country: state.customer.billingInfo.country,
        customerId: result.customerId,
        smsConsent: state.customer.smsConsent
      }
    }
  },
  setCustomer(state, form) {
    const customerData = { ...form }
    delete customerData.quantity
    delete customerData.productId
    delete customerData.creditCardInfo
    delete customerData.promoCode

    state.customer = customerData
  },
  SET_QUERY_PARAMS(state, params) {
    state.queryParameters = params
  },
  SET_QUERY_STRING(state, queryString) {
    state.queryString = queryString
  },
  SET_ECOMMERCE(state, value) {
    state.ecommerce = value
  },
  setBusy(state, busy) {
    state.busy = busy
  },
  setSessionId(state, sessionId) {
    state.sessionId = sessionId
    state.analytics.sessionId = sessionId
  },
  setSMSOptIn(state, isOptIn) {
    state.smsOptIn = isOptIn
  },
  INCREMENT_PURCHASE_ATTEMPTS(state) {
    state.purchaseAttempts++
  }
}

export const actions = {
  unsetEcommerce({ commit }) {
    commit('SET_ECOMMERCE', undefined)
  },
  updateQueryParams({ commit, state }, params) {
    if (isEmptyObject(params)) return

    const existing = state.queryParameters || {}
    const lowerParams = {}

    Object.keys(existing).forEach(p => {
      if (p) {
        lowerParams[p.toLowerCase()] = existing[p]
      }
    })

    Object.keys(params).forEach(p => {
      if (p) {
        lowerParams[p.toLowerCase()] = params[p]
      }
    })

    commit('SET_QUERY_PARAMS', lowerParams)

    const array = Object.entries(lowerParams).map(
      ([key, value]) => `${key}=${value}`
    )
    const queryString = array.length > 0 ? '?' + array.join('&') : ''
    if (queryString.length > 1) {
      commit('SET_QUERY_STRING', queryString)
    }
  },
  sendGTMEvent({ state, rootGetters }, { event, payload }) {
    this.$gtm.push({
      event,
      ...payload,
      queryParams: state.queryParameters,
      funnelData: rootGetters['analytics/analytics'].source.platform.funnelData,
      splitTestVariant:
        rootGetters['split-testing/testId'] +
        '-' +
        rootGetters['split-testing/variantId'],
      sessionId: state.sessionId,
      ecommerce: state.ecommerce
    })
  },
  enterPage({ commit, state }, route) {
    commit('SET_PAGE', route)
  },
  pageEnter({ dispatch, state }) {
    dispatch('analytics/setFunnelData', {
      event: 'PageEnter',
      brand: state.brand,
      productLine: state.route.productLine,
      funnelName: state.route.funnel,
      stepName: state.step.id,
      type: state.step.type,
      version: ''
    })
    dispatch('analytics/setUrlParams')
    dispatch('analytics/setTechCollection')
    dispatch('analytics/setAffiliate', {
      id: state.queryParameters.afid || null,
      campaignId:
        state.queryParameters.aff_sub || state.queryParameters.source || null
    })
    dispatch('analytics/setContentEnv', state.config.contentEnv)
  },
  pageLeave({ dispatch }) {
    return dispatch('analytics/setTechCollection')
  },
  viewportChange({ dispatch }) {
    return dispatch('analytics/setTechCollection')
  },
  init({ commit, state }) {
    if (state.sessionId === null) {
      commit(
        'setSessionId',
        'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = (Math.random() * 16) | 0
          const v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        })
      )
    }
  },
  initSplitTest({ state, dispatch, commit, getters }, { slug, salesApi }) {
    if (getters['split-testing/inSplitTest']) {
      salesApi
        .step(slug, getters['split-testing/splitTestFunnel'], state.config)
        .then(({ funnel }) => {
          commit('enterStep', funnel)
          dispatch('validateStep')
          commit('setGoogleCategory', funnel.googleCategory)
        })
    }
  },
  nuxtServerInit({ commit, state, $config }, context) {
    if (Object.keys(state.config).length === 0) {
      commit('setContext', context.$config)
    }
  },
  purchase({ commit, state, dispatch, getters }, form = null) {
    const MAX_PURCHASE_ATTEMPTS = 4

    if (state.purchaseAttempts >= MAX_PURCHASE_ATTEMPTS) {
      const errMsg =
        'Maximum payment attempts reached. Please reach out to us at ' +
        '<a href="tel:18003044202" style="color:white; font-weight:bold;">1-800-304-4202</a>' +
        ' or via live chat to complete your order'
      commit('setPaymentError', `Error - ${errMsg}`)
      return Promise.resolve({ errorCode: 'MAX_ATTEMPTS', errMsg })
    }

    const [fbp, fbc] = getCookie(['_fbp', '_fbc'])
    if (fbp && fbc) {
      commit('SET_QUERY_PARAMS', {
        ...state.queryParameters,
        fb: `${fbp}::${fbc}`
      })
    }
    if (form.resubmit) {
      commit('CLEAR_PAYMENT_TOKEN')
    }
    const order = {
      paymentToken: state.paymentToken,
      slug: state.slug,
      queryParameters: state.queryParameters,
      sessionId: state.sessionId,
      ...state.customer,
      ...form
    }
    if (form.updatePhone) {
      commit('updatePhone', form.updatePhone)
    }

    commit('setBusy', true)

    return this.$salesApi
      .order(order, getters['split-testing/splitTestFunnel'])
      .then(result => {
        if (result.paymentToken && state.paymentToken == null) {
          commit('setPaymentToken', result.paymentToken)
        }

        result.confirmation.pricing = getters.pricing(
          order.productId,
          order.quantity
        )
        result.confirmation.offer = getters.offer(
          order.productId,
          order.quantity
        )
        commit('addOrder', result.confirmation)

        Promise.all([
          dispatch('analytics/setFunnelData', {
            brand: state.brand,
            productLine: state.route.productLine,
            funnelName: state.route.funnel,
            stepName: state.step.id,
            type: state.step.type,
            version: ''
          }),
          dispatch('analytics/setAffiliate', {
            id: state.queryParameters.afid || null,
            campaignId:
              state.queryParameters.aff_sub ||
              state.queryParameters.source ||
              null
          }),
          dispatch('analytics/setOrder', {
            id: result.confirmation.orderId,
            currency: 'USD',
            subtotal: result.confirmation.pricing.subTotal,
            grandTotal: result.confirmation.pricing.grandTotal,
            promoCode: result.confirmation.promoCode,
            gpmTotal: result.confirmation.offer.pricing.metric1,
            items: [
              {
                name: result.confirmation.offer.name,
                id: result.confirmation.offer.product.id,
                price: result.confirmation.offer.pricing.displayPrice,
                shipping: result.confirmation.pricing.shippingTotal,
                brand: state.config.brand,
                category: state.route.productLine,
                quantity: result.confirmation.quantity
              }
            ]
          }),
          dispatch('analytics/setCustomer', {
            firstName: result.confirmation.firstName,
            lastName: result.confirmation.lastName,
            email: result.confirmation.email,
            phone: result.confirmation.phone,
            billing: {
              address: result.confirmation.billingInfo.address,
              city: result.confirmation.billingInfo.city,
              country: result.confirmation.billingInfo.country,
              state: result.confirmation.billingInfo.state,
              zip: result.confirmation.billingInfo.zip
            },
            shipping: {
              address: result.confirmation.shippingInfo.address,
              city: result.confirmation.shippingInfo.city,
              country: result.confirmation.shippingInfo.country,
              state: result.confirmation.shippingInfo.state,
              zip: result.confirmation.shippingInfo.zip
            },
            customerId: result.confirmation.customerId,
            smsConsent: result.confirmation.smsConsent
          })
        ])
          .then(() => {
            dispatch('analytics/sendEvent', 'Purchase')
            dispatch('sendGTMEvent', { event: 'Purchase' })
            if (
              this.$config.environment !== 'production' ||
              state.ecommerce.purchase.actionField.livePurchase
            ) {
              this.$blotout.sendUserData({
                email: result.confirmation.email,
                firstName: result.confirmation.firstName,
                lastName: result.confirmation.lastName,
                phone: result.confirmation.phone
              })
              this.$blotout.sendEvent('Purchase', {
                currency: 'USD',
                value:
                  result.confirmation.offer.pricing.metric1 *
                  result.confirmation.pricing.quantity, // get the gpm
                orderId: result.confirmation.orderId,
                eventId: result.confirmation.orderId,
                contents: [
                  {
                    title: result.confirmation.offer.name,
                    id: result.confirmation.offer.product.id,
                    item_price: result.confirmation.offer.pricing.displayPrice,
                    category: state.route.productLine,
                    quantity: result.confirmation.quantity
                  }
                ]
              })
            }
            dispatch('nextStep', {
              acceptOffer: true,
              productId: order.productId
            })
          })
          .finally(() => {
            commit('setBusy', false)
          })

        return result
      })
      .catch(({ response }) => {
        const shipState = state.customer.shippingInfo.state
        let akHiExclusion = false
        const offers = getters.offers
        for (let i = 0; i < offers.length; ++i) {
          const exclusions = offers[i].shipping.exclusions
          const productId = offers[i].product.id
          if (parseInt(order.productId) === parseInt(productId)) {
            if (exclusions.includes(shipState)) {
              akHiExclusion = true
            }
            break
          }
        }
        const responseData = response && response.data && response.data.data
        const { errorCode, errorMessage } = responseData || {}
        const errCode = errorCode || 16008
        let errMsg =
          errorMessage ||
          'We are unable to process your order at this time. Please reach out to us at ' +
            '<a href="tel:18003044202" style="color:white; font-weight:bold;">1-800-304-4202</a>' +
            ' or via live chat to complete your order'
        if (errCode === 800) {
          if (akHiExclusion) {
            errMsg =
              '<strong>This item cannot be shipped to the state selected.</strong> Please adjust the shipping address or reach out to us at ' +
              '<a href="tel:18003044202" style="color:white; font-weight:bold;">1-800-304-4202</a>' +
              ' or via live chat to complete your order'
          } else {
            errMsg =
              'There was an issue processing your payment. Please reach out to us at ' +
              '<a href="tel:18003044202" style="color:white; font-weight:bold;">1-800-304-4202</a>' +
              ' or via live chat to complete your order'
          }
        }
        commit('setBusy', false)
        commit('INCREMENT_PURCHASE_ATTEMPTS')
        if (state.purchaseAttempts >= MAX_PURCHASE_ATTEMPTS) {
          errMsg =
            'Maximum payment attempts reached. Please reach out to us at ' +
            '<a href="tel:18003044202" style="color:white; font-weight:bold;">1-800-304-4202</a>' +
            ' or via live chat to complete your order'
          commit('setPaymentError', `Error - ${errMsg}`)
        } else {
          commit('setPaymentError', `Error ${errCode} - ${errMsg}`)
        }
        return { errorCode: errCode, errMsg }
      })
  },
  firePurchaseAnalytics({ state, dispatch }, confirmation) {
    return Promise.all([
      dispatch('analytics/setFunnelData', {
        brand: state.brand,
        productLine: state.route.productLine,
        funnelName: state.route.funnel,
        stepName: state.step.id,
        type: state.step.type,
        version: ''
      }),
      dispatch('analytics/setAffiliate', {
        id: state.queryParameters.afid || null,
        campaignId:
          state.queryParameters.aff_sub || state.queryParameters.source || null
      }),
      dispatch('analytics/setOrder', {
        id: confirmation.orderId,
        currency: 'USD',
        subtotal: confirmation.pricing.subTotal,
        grandTotal: confirmation.pricing.grandTotal,
        promoCode: confirmation.promoCode,
        gpmTotal: confirmation.offer.pricing.metric1,
        items: [
          {
            name: confirmation.offer.name,
            id: confirmation.offer.product.id,
            price: confirmation.offer.pricing.displayPrice,
            shipping: confirmation.pricing.shippingTotal,
            brand: state.config.brand,
            category: state.route.productLine,
            quantity: confirmation.quantity
          }
        ]
      }),
      dispatch('analytics/setCustomer', {
        firstName: confirmation.firstName,
        lastName: confirmation.lastName,
        email: confirmation.email,
        phone: confirmation.phone,
        billing: {
          address: confirmation.billingInfo.address,
          city: confirmation.billingInfo.city,
          country: confirmation.billingInfo.country,
          state: confirmation.billingInfo.state,
          zip: confirmation.billingInfo.zip
        },
        shipping: {
          address: confirmation.shippingInfo.address,
          city: confirmation.shippingInfo.city,
          country: confirmation.shippingInfo.country,
          state: confirmation.shippingInfo.state,
          zip: confirmation.shippingInfo.zip
        },
        customerId: confirmation.customerId,
        smsConsent: confirmation.smsConsent
      })
    ]).then(() => {
      dispatch('analytics/sendEvent', 'Purchase')
      dispatch('sendGTMEvent', { event: 'Purchase' })
      if (
        this.$config.environment !== 'production' ||
        state.ecommerce.purchase.actionField.livePurchase
      ) {
        this.$blotout.sendUserData({
          email: confirmation.email,
          firstName: confirmation.firstName,
          lastName: confirmation.lastName,
          phone: confirmation.phone
        })
        this.$blotout.sendEvent('Purchase', {
          currency: 'USD',
          value:
            confirmation.offer.pricing.metric1 * confirmation.pricing.quantity, // get the gpm
          orderId: confirmation.orderId,
          eventId: confirmation.orderId,
          contents: [
            {
              title: confirmation.offer.name,
              id: confirmation.offer.product.id,
              item_price: confirmation.offer.pricing.displayPrice,
              category: state.route.productLine,
              quantity: confirmation.quantity
            }
          ]
        })
      }
    })
  },
  nextStep({ state, getters }, { acceptOffer, productId, isSplitit = false }) {
    const offer = getters.offer(productId)
    const next = acceptOffer
      ? (isSplitit && offer && offer.acceptSplitit) || (offer && offer.accept)
      : state.step.decline

    const path = state.route.productLine + '/' + state.route.funnel + '/' + next

    if (path !== state.slug) {
      this.$router.push({ path: '/' + path.toLowerCase() })
    }
  },
  validateStep({ state, dispatch, getters }) {
    const offers = getters.offers
    for (let i = 0; i < offers.length; ++i) {
      const exclusions = offers[i].shipping.exclusions
      if (
        (exclusions.includes('AK') &&
          state.customer.shippingInfo.state === 'AK') ||
        (exclusions.includes('HI') &&
          state.customer.shippingInfo.state === 'HI') ||
        (exclusions.includes('Canada') &&
          state.customer.shippingInfo.country === 'CA')
      ) {
        dispatch('nextStep', { acceptOffer: false, productId: false })
        break
      }
    }
  },
  getStep(
    { state, dispatch, commit, getters, $salesApi, $config },
    { slug, salesApi }
  ) {
    salesApi
      .step(slug, getters['split-testing/splitTestFunnel'], state.config)
      .catch(err => {
        throw new Error(err.message)
      })
      .then(({ funnel }) => {
        if (funnel) {
          commit('enterStep', funnel)
          dispatch('validateStep')
          commit('setGoogleCategory', funnel.googleCategory)
        }
      })
  },
  async getStory(
    { state, dispatch, commit, getters, $salesApi, $config },
    { slug, salesApi }
  ) {
    return await salesApi
      .page(slug, getters['split-testing/splitTestFunnel'], state.config)
      .catch(err => {
        throw new Error(err.message)
      })
      .then(({ funnel, story: { data: story } }) => {
        if (slug === 'home/error') {
          return Promise.resolve(story)
        }
        if (story) {
          commit('enterStory', story)
        }
        if (funnel) {
          commit('enterStep', funnel)
          dispatch('validateStep')
          commit('setGoogleCategory', funnel.googleCategory)
        }
      })
  }
}
